import { createAddItemLocation, createUpdate } from "../../actions";
import ProofType from "../../models/ProofType";
import { createUpdateItemDecoration } from "../../actions";
import { createAddArtwork, createUpdateArtwork } from "../../actions/artwork";
import type { ProductDecorationRequest } from "../../services/mockupGeneration";
import * as itemActions from "../../actions/item";
import {
  getCanonicalArtworks,
  getCanonicalDecorations,
  type ItemLike,
  type ItemArtworkLike,
  type ItemDecorationLike,
} from "./decorationModel";
import { normalizeString, readString } from "./mockupUtils";
import {
  extractAddedLocationId,
  extractUpdatedItem,
  buildPersistenceResult,
  type PersistDecorationUpdatesResult,
} from "./persistenceUtils";

/**
 * Handles saving user-selected decoration information back to the item.
 *
 * SCENARIOS:
 * ----------
 * 1. Existing Location:
 *    - User picked a location that already exists on the item
 *    - Directly updates decoration and artwork at that location
 *
 * 2. New Location (PS Config Mode):
 *    - User picked a PS Config location not yet on the item
 *    - Creates new item_location first
 *    - Then updates decoration and artwork at the new location
 *
 * WHAT GETS SAVED:
 * ----------------
 * - Decoration method (ext_decoration_id, imprint_name)
 * - Artwork association (links logo file to location)
 * - Location metadata (ext_location_id, location_name)
 * =============================================================================
 */

type Dispatch = (action: unknown) => unknown;

type ExtLocationArg = { ps_location_id: string | number; location_name: string };

type FullItemLike = ItemLike & {
  item_locations?: Array<{
    item_decorations?: ItemDecorationLike[] | null;
    decorations?: ItemDecorationLike[] | null;
    artworks?: ItemArtworkLike[] | null;
  }> | null;
};

type PersistPickedDecoration = {
  item_decoration_id?: string | null;
  imprint_id?: string | null;
  imprint_name?: string | null;
  ext_decoration_id?: string | number | null;
  ext_location_id?: string | number | null;
};

type PersistTask = { label: string; promise: Promise<unknown> };

const updateItemLocation = createUpdate("item-location", {
  request: itemActions.UPDATE_ITEM_LOCATION_REQUEST,
  success: itemActions.UPDATE_ITEM_LOCATION_SUCCESS,
  failure: itemActions.UPDATE_ITEM_LOCATION_FAILURE,
  failure_message: "Unable to update item location",
});

function computeNextLocationTitle(args: {
  currentTitle: string | null;
  nextDisplayName: string;
}): string {
  const { currentTitle, nextDisplayName } = args;
  const prefixMatch = (currentTitle ?? "").match(/^(Location\s+\d+::\s*)/i);
  const prefix = prefixMatch?.[1] ?? "";
  return `${prefix}${nextDisplayName}`.trim();
}

function selectDecorationToUpdate(args: {
  decorationsAtLocation: ItemDecorationLike[];
  pickedItemDecorationId: string | null;
  pickedImprintId: string | null;
}): ItemDecorationLike | undefined {
  const { decorationsAtLocation, pickedItemDecorationId, pickedImprintId } = args;

  if (pickedItemDecorationId) {
    const byItemDecorationId = decorationsAtLocation.find((d) => d.item_decoration_id === pickedItemDecorationId);
    if (byItemDecorationId) return byItemDecorationId;
  }

  if (pickedImprintId) {
    const byImprintId = decorationsAtLocation.find((d) => d.imprint_id === pickedImprintId);
    if (byImprintId) return byImprintId;
  }

  return decorationsAtLocation[0];
}

function buildDecorationUpdateTask(args: {
  dispatch: (action: unknown) => unknown;
  decorationToUpdate: ItemDecorationLike | undefined;
  pickedImprintId: string | null;
  pickedExtDecorationId: string | number | null;
  pickedExtLocationId: string | number | null;
  pickedImprintName: string | null;
  warnings: string[];
}): PersistTask | null {
  const {
    dispatch,
    decorationToUpdate,
    pickedImprintId,
    pickedExtDecorationId,
    pickedExtLocationId,
    pickedImprintName,
    warnings,
  } = args;

  if (!decorationToUpdate) return null;

  const itemDecorationId =
    typeof decorationToUpdate.item_decoration_id === "string"
      ? decorationToUpdate.item_decoration_id
      : null;
  if (!itemDecorationId) {
    warnings.push("Unable to persist decoration changes: missing item_decoration_id.");
    return null;
  }

  // PromoStandards-style decorations: persist ext_* + imprint_name via ps_imprint when present.
  if (pickedExtDecorationId != null && pickedExtLocationId != null && (pickedImprintName || pickedImprintId)) {
    const changed =
      decorationToUpdate.ext_decoration_id !== pickedExtDecorationId
      || decorationToUpdate.ext_location_id !== pickedExtLocationId
      || (pickedImprintName != null && decorationToUpdate.imprint_name !== pickedImprintName);
    if (!changed) return null;

    const promise = dispatch(
      createUpdateItemDecoration(
        itemDecorationId,
        "ps_imprint",
        decorationToUpdate.imprint_id,
        {
          ext_decoration_id: pickedExtDecorationId,
          ext_location_id: pickedExtLocationId,
          imprint_name: pickedImprintName || pickedImprintId || "",
        },
      ),
    ) as Promise<unknown>;

    return { label: "decoration update", promise };
  }

  // Standard: persist imprint_id.
  if (pickedImprintId && decorationToUpdate.imprint_id !== pickedImprintId) {
    const promise = dispatch(
      createUpdateItemDecoration(
        itemDecorationId,
        "imprint_id",
        decorationToUpdate.imprint_id,
        pickedImprintId,
      ),
    ) as Promise<unknown>;

    return { label: "decoration update", promise };
  }

  return null;
}

function selectArtworkToUpdate(args: {
  artworksAtLocation: ItemArtworkLike[];
  pickedImprintId: string | null;
}): ItemArtworkLike | undefined {
  const { artworksAtLocation, pickedImprintId } = args;

  if (pickedImprintId) {
    const byImprintId = artworksAtLocation.find((a) => a.imprint_id === pickedImprintId);
    if (byImprintId) return byImprintId;
  }

  return artworksAtLocation[0];
}

function buildArtworkUpdateTask(args: {
  dispatch: (action: unknown) => unknown;
  locationId: string;
  artworksAtLocation: ItemArtworkLike[];
  pickedImprintId: string | null;
  pickedImprintName: string | null;
  pickedItemDecorationId: string | null;
  logoFileId: string | null;
  warnings: string[];
}): PersistTask | null {
  const { dispatch, locationId, artworksAtLocation, pickedImprintId, pickedImprintName, pickedItemDecorationId, logoFileId, warnings } = args;

  if (!logoFileId) return null;

  const artworkToUpdate = selectArtworkToUpdate({ artworksAtLocation, pickedImprintId });

  if (artworkToUpdate) {
    const artworkId = typeof artworkToUpdate.artwork_id === "string" ? artworkToUpdate.artwork_id : null;
    if (!artworkId) {
      warnings.push("Unable to persist artwork changes: missing artwork_id.");
      return null;
    }

    const fileMatches = artworkToUpdate.artwork_file_id === logoFileId;
    const imprintMatches = pickedImprintId ? artworkToUpdate.imprint_id === pickedImprintId : true;
    if (fileMatches && imprintMatches) return null;

    const promise = dispatch(
      createUpdateArtwork({
        artwork_id: artworkId,
        artwork_file_id: logoFileId,
        imprint_id: pickedImprintId || artworkToUpdate.imprint_id,
        item_decoration_id: pickedItemDecorationId,
        artwork_imprint_type: pickedImprintName,
        item_location_id: locationId,
      }),
    ) as Promise<unknown>;

    return { label: "artwork update", promise };
  }

  const promise = dispatch(
    createAddArtwork({
      item_location_id: locationId,
      item_decoration_id: pickedItemDecorationId,
      imprint_id: pickedImprintId,
      artwork_imprint_type: pickedImprintName,
      proof_id: ProofType.None,
      artwork_file_id: logoFileId,
      artwork_title: "AI Mockup Artwork",
    }),
  ) as Promise<unknown>;

  return { label: "artwork update", promise };
}

async function runTasksAndCollectWarnings(args: {
  tasks: PersistTask[];
  warnings: string[];
}): Promise<PersistDecorationUpdatesResult> {
  const { tasks, warnings } = args;

  const results = await Promise.allSettled(tasks.map((t) => t.promise));
  let anySucceeded = false;
  results.forEach((r, idx) => {
    if (r.status === "rejected") {
      const label = tasks[idx]?.label ?? "update";
      warnings.push(`Failed to persist ${label}. Please retry.`);
      console.warn("Failed to persist decoration updates:", label, r.reason);
    } else {
      anySucceeded = true;
    }
  });

  return buildPersistenceResult({ updated: anySucceeded, warnings });
}

async function persistDecorationUpdates(args: {
  dispatch: (action: unknown) => unknown;
  item: FullItemLike | null | undefined;
  locationId: string | null | undefined;
  pickedDecoration:
    | PersistPickedDecoration
    | null
    | undefined;
  logoFileId: string | null | undefined;
}): Promise<PersistDecorationUpdatesResult> {
  const { dispatch, item, locationId, pickedDecoration, logoFileId } = args;
  if (!item || !locationId || !pickedDecoration) {
    return buildPersistenceResult({ updated: false });
  }

  const pickedLocationName = readString((pickedDecoration as Record<string, unknown>)?.location_name) ?? null;
  const pickedImprintId = pickedDecoration.imprint_id ?? null;
  const pickedItemDecorationId = pickedDecoration.item_decoration_id ?? null;
  const pickedExtDecorationId = pickedDecoration.ext_decoration_id ?? null;
  const pickedExtLocationId = pickedDecoration.ext_location_id ?? null;
  const pickedImprintName = pickedDecoration.imprint_name ?? null;

  const itemDecorations = getCanonicalDecorations(item);
  const artworks = getCanonicalArtworks(item);

  const warnings: string[] = [];

  const locationObj = Array.isArray(item.item_locations)
    ? item.item_locations.find((l) => (l as { item_location_id?: string | null })?.item_location_id === locationId) ?? null
    : null;
  const currentTitle =
    locationObj && typeof (locationObj as { item_location_title?: string | null })?.item_location_title === "string"
      ? (locationObj as { item_location_title?: string | null }).item_location_title!
      : null;
  if (pickedLocationName) {
    const currentNormalized = normalizeString(currentTitle ?? "");
    const nextNormalized = normalizeString(pickedLocationName);
    if (currentNormalized && currentNormalized !== nextNormalized) {
      const nextTitle = computeNextLocationTitle({ currentTitle, nextDisplayName: pickedLocationName });
      const promise = dispatch(
        updateItemLocation(locationId, "item_location_title", currentTitle ?? "", nextTitle)
      ) as Promise<unknown>;
      warnings.push("Location title will be updated to match the mockup location name.");
      // ensure it runs alongside other tasks
      // We'll add it to tasks below.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }
  }

  const decorationsAtLocation = itemDecorations.filter((d) => d.item_location_id === locationId);
  const decorationToUpdate = selectDecorationToUpdate({
    decorationsAtLocation,
    pickedItemDecorationId,
    pickedImprintId,
  });

  const decorationTask = buildDecorationUpdateTask({
    dispatch,
    decorationToUpdate,
    pickedImprintId,
    pickedExtDecorationId,
    pickedExtLocationId,
    pickedImprintName,
    warnings,
  });

  const artworksAtLocation = artworks.filter((a) => a.item_location_id === locationId);
  const artworkTask = buildArtworkUpdateTask({
    dispatch,
    locationId,
    artworksAtLocation,
    pickedImprintId,
    pickedImprintName,
    pickedItemDecorationId: decorationToUpdate?.item_decoration_id ,
    logoFileId,
    warnings,
  });

  const tasks: PersistTask[] = [];

  if (pickedLocationName) {
    const currentNormalized = normalizeString(currentTitle ?? "");
    const nextNormalized = normalizeString(pickedLocationName);
    if (currentNormalized !== nextNormalized) {
      const nextTitle = computeNextLocationTitle({ currentTitle, nextDisplayName: pickedLocationName });
      const promise = dispatch(
        updateItemLocation(locationId, "item_location_title", currentTitle ?? "", nextTitle)
      ) as Promise<unknown>;
      tasks.push({ label: "location title update", promise });
    }
  }
  if (decorationTask) tasks.push(decorationTask);
  if (artworkTask) tasks.push(artworkTask);

  if (tasks.length === 0) {
    return buildPersistenceResult({ updated: false, warnings });
  }

  return runTasksAndCollectWarnings({ tasks, warnings });
}

export async function persistMockupPresetIfRequested(args: {
  dispatch: Dispatch;
  itemId: string | null | undefined;
  item: ItemLike | null | undefined;
  pickedDecoration: ProductDecorationRequest | null | undefined;
  logoFileId: string | null | undefined;
  saveUpdates: boolean;
}): Promise<PersistDecorationUpdatesResult> {
  const { dispatch, itemId, item, pickedDecoration, logoFileId, saveUpdates } = args;
  if (!saveUpdates) return buildPersistenceResult({ updated: false });

  if (!itemId || !item || !pickedDecoration) {
    return buildPersistenceResult({
      updated: false,
      warnings: ["Unable to save updates: missing item or decoration selection."],
    });
  }

  const pickedLocationId = readString(pickedDecoration.item_location_id);
  if (pickedLocationId) {
    return persistDecorationUpdates({
      dispatch,
      item: item as FullItemLike,
      locationId: pickedLocationId,
      pickedDecoration,
      logoFileId,
    });
  }

  const extLocationId = pickedDecoration.ext_location_id ?? null;
  const locationName = readString(pickedDecoration.location_name) ?? "No Name";
  // Unstructured/3rd-party branch: create a plain item location using free-text location name.
  if (extLocationId == null) {
    const addResult = await (dispatch(createAddItemLocation(itemId, null, null, null, null, locationName)) as Promise<unknown>);
    const newLocationId = extractAddedLocationId({ result: addResult, extLocationId: null });
    const updatedItem = extractUpdatedItem(addResult) ?? item;
    const warnings: string[] = [];
    if (!newLocationId) {
      return buildPersistenceResult({
        updated: false,
        warnings: ["Location was added, but we could not determine the new item location id to save decoration details."],
      });
    }

    const persisted = await persistDecorationUpdates({
      dispatch,
      item: updatedItem as FullItemLike,
      locationId: newLocationId,
      pickedDecoration,
      logoFileId,
    });

    warnings.push(...(persisted.warnings ?? []));
    if (!persisted.updated) {
      warnings.push("Location was added, but no decoration preset could be updated. You may need to set the imprint on the item page.");
    }
    return buildPersistenceResult({ updated: persisted.updated, warnings });
  }

  const extLocation: ExtLocationArg = { ps_location_id: extLocationId, location_name: locationName };
  const addResult = await (dispatch(createAddItemLocation(itemId, null, null, null, extLocation)) as Promise<unknown>);

  const newLocationId = extractAddedLocationId({ result: addResult, extLocationId });
  const updatedItem = extractUpdatedItem(addResult) ?? item;
  const warnings: string[] = [];

  if (!newLocationId) {
    return buildPersistenceResult({
      updated: false,
      warnings: ["Location was added, but we could not determine the new item location id to save decoration details."],
    });
  }

  const persisted = await persistDecorationUpdates({
    dispatch,
    item: updatedItem as FullItemLike,
    locationId: newLocationId,
    pickedDecoration,
    logoFileId,
  });

  warnings.push(...(persisted.warnings ?? []));
  if (!persisted.updated) {
    warnings.push("Location was added, but no decoration preset could be updated. You may need to set the imprint on the item page.");
  }

  return buildPersistenceResult({ updated: persisted.updated, warnings });
}


